import { useState, useEffect } from 'react';

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

interface PriceCard {
  name: string;
  price: string;
  priceLabel?: string;
  from?: boolean;
  period: string;
  cardNote?: string;
  features: string[];
  featured?: boolean;
  priority?: boolean;
  ctaText?: string;
}

interface Tab {
  key: string;
  label: string;
  cards: PriceCard[];
}

const MONTHLY_TABS = ['marketing', 'socialne', 'sprava'];

const TABS: Tab[] = [
  {
    key: 'weby', label: 'Weby',
    cards: [
      {
        name: 'Štartovací', price: '490', period: 'jednorazovo · do 5 strán',
        features: ['Mobilný responzívny dizajn','Základné SEO nastavenie','Kontaktný formulár','Google Analytics','Dodanie do 7 dní'],
      },
      {
        name: 'Profesionálny', price: '890', period: 'jednorazovo · do 8 strán', featured: true,
        features: ['Prémiový custom dizajn','Pokročilé SEO & rýchlosť','Blog / CMS systém','Animácie & interakcie','Viacjazyčnosť (SK/EN)','30 dní podpora zadarmo'],
      },
      {
        name: 'Firemný', price: '1490', from: true, period: 'jednorazovo · na mieru',
        features: ['Plne custom dizajn & vývoj','Pokročilý CMS (headless)','E-mail marketing integrácia','Neobmedzené strany','Prioritná podpora 90 dní'],
      },
    ],
  },
  {
    key: 'eshopy', label: 'E-shopy',
    cards: [
      {
        name: 'Mini', price: '790', period: 'do 20 produktov',
        features: ['WooCommerce setup','Platobná brána (Stripe/PayPal)','Základný custom dizajn','Správa produktov','Email notifikácie objednávok'],
      },
      {
        name: 'Štandard', price: '1490', period: 'do 100 produktov', featured: true,
        features: ['Vlastný prémiový dizajn','Pokročilý admin panel','Skladová dostupnosť','Napojenie na kuriérov','30 dní podpora'],
      },
      {
        name: 'Custom', price: '2490', from: true, period: 'vlastné funkcie',
        features: ['Rezervačný systém na mieru','Vlastný košík & platby','CRM napojenie','API integrácie','Prioritná podpora'],
      },
    ],
  },
  {
    key: 'marketing', label: 'Marketing',
    cards: [
      {
        name: 'Štart', price: '199', period: '1 platforma',
        cardNote: 'Odporúčaný ad budget: min. 150€/mes',
        features: ['Google Ads ALEBO Meta Ads','Kampaňový setup','Mesačný report','Optimalizácia kampaní'],
      },
      {
        name: 'Rast', price: '349', period: '2 platformy', featured: true,
        cardNote: 'Odporúčaný ad budget: min. 300€/mes',
        features: ['Google Ads + Meta Ads','A/B testovanie','Pokročilá analytika','Retargeting kampane','Týždenný report'],
      },
      {
        name: 'Pro', price: '549', period: 'full funnel',
        cardNote: 'Odporúčaný ad budget: min. 500€/mes',
        features: ['Všetky platformy','Kompletná stratégia','Dedikovaný account manager','Denný monitoring'],
      },
    ],
  },
  {
    key: 'socialne', label: 'Sociálne siete',
    cards: [
      {
        name: 'Štart', price: '249', period: '1 sieť',
        features: ['8 príspevkov/mes','Grafické spracovanie','Plánovanie & publikovanie','Základná správa komentárov'],
      },
      {
        name: 'Rast', price: '399', period: '2 siete', featured: true,
        features: ['12 príspevkov/mes','Stories & Reels','Správa komunity','Mesačný report & stratégia'],
      },
      {
        name: 'Pro', price: '599', period: '3 siete',
        features: ['20 príspevkov/mes','Reklamy setup (Meta Ads)','Influencer spolupráce','Prioritná komunikácia'],
      },
    ],
  },
  {
    key: 'sprava', label: 'Správa',
    cards: [
      {
        name: 'Základný', price: '49', period: 'mesačná správa',
        features: ['Mesačné aktualizácie','Denné zálohy','Uptime monitoring','SSL monitoring'],
      },
      {
        name: 'Štandard', price: '89', period: 'mesačná správa', featured: true,
        features: ['Všetko zo Základného','2h technická podpora/mes','Rýchle opravy & bugfixy','Mesačný report'],
      },
      {
        name: 'Priority', price: '199', period: 'mesačná správa', priority: true,
        features: ['Všetko zo Štandard','5h úprav/mes','Odpoveď do 2 hodín','Dostupnosť 7 dní v týždni','🟢 Prednostná obsluha'],
      },
    ],
  },
  {
    key: 'crm', label: 'CRM',
    cards: [
      {
        name: 'Základný CRM', price: '390', period: 'jednorazovo',
        features: ['Správa klientov & kontaktov','Projektový prehľad','Email šablóny & mailing','Základné reporty','Dodanie do 5 dní'],
      },
      {
        name: 'Rozšírený systém', price: '890', period: 'jednorazovo', featured: true,
        features: ['Vlastný CRM na mieru','Automatické notifikácie','Integrácia s webom','Používateľské role & práva','Školenie & dokumentácia'],
      },
      {
        name: 'CRM na mieru', price: '', priceLabel: 'Cena po konzultácii', period: 'individuálna ponuka',
        ctaText: 'Konzultácia zadarmo →',
        features: ['Plne custom riešenie','API integrácie','Pokročilé automatizácie','Napojenie na externé systémy','Dlhodobá podpora & rozvoj'],
      },
    ],
  },
];

export default function Pricing() {
  const [activeTab, setActiveTab] = useState('weby');

  useEffect(() => {
    const panel = document.getElementById(`tab-${activeTab}`);
    if (!panel) return;
    const els = panel.querySelectorAll<Element>('.fade-up');
    els.forEach((el) => el.classList.remove('visible'));
    const t = setTimeout(() => {
      els.forEach((el) => el.classList.add('visible'));
    }, 50);
    return () => clearTimeout(t);
  }, [activeTab]);

  const isMonthly = MONTHLY_TABS.includes(activeTab);

  return (
    <section id="ceny" aria-labelledby="ceny-heading">
      <div className="container">
        <div className="section-header">
          <span className="section-tag fade-up">Ceny</span>
          <h2 id="ceny-heading" className="section-h2 fade-up delay-1">
            <span className="grad">Transparentné</span> ceny
          </h2>
          <p className="section-sub fade-up delay-2">
            Žiadne skryté poplatky. Vyber si balík, ktorý zodpovedá tvojim cieľom.
          </p>
          <p className="pricing-disclaimer fade-up delay-3">
            * K cene správy si klient pripravuje vlastný reklamný rozpočet (ide priamo platforme Google/Meta, nie nám)
          </p>
        </div>

        <div className="pricing-tabs" role="tablist" aria-label="Kategórie služieb">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
              role="tab"
              aria-selected={activeTab === t.key}
              aria-controls={`tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {TABS.map((tab) => (
          <div
            key={tab.key}
            id={`tab-${tab.key}`}
            className={`pricing-panel${activeTab === tab.key ? ' active' : ''}`}
            role="tabpanel"
          >
            <div className="pricing-grid fade-up">
              {tab.cards.map((c) => (
                <div
                  key={c.name}
                  className={`price-card${c.featured ? ' featured' : ''}${c.priority ? ' priority' : ''}`}
                >
                  {c.priority && <div className="price-priority-badge">🟢 Priority klient</div>}
                  <div className="price-name">{c.name}</div>
                  {c.priceLabel ? (
                    <div className="price-consult">{c.priceLabel}</div>
                  ) : (
                    <div className="price-amount">
                      {c.from && <span className="price-from">od</span>}
                      <span className="price-val">{c.price}</span>
                      <span className="price-suffix">{isMonthly ? '€/mes' : '€'}</span>
                    </div>
                  )}
                  <div className="price-period">{c.period}</div>
                  {c.cardNote && <div className="price-card-note">{c.cardNote}</div>}
                  <div className="price-divider" />
                  <ul className="price-features">
                    {c.features.map((f) => (
                      <li key={f} className="price-feature">
                        <CheckIcon />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a href="#cta-section" className="price-cta">
                    {c.ctaText ?? 'Začíname →'}
                  </a>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
